// frontend/src/utils/constants.ts

// ============================================
// API и URL
// ============================================

export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'
export const WS_BASE_URL = import.meta.env.VITE_WS_URL || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`

export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/v1/auth/login',
    REGISTER: '/v1/auth/register',
    REFRESH: '/v1/auth/refresh',
    LOGOUT: '/v1/auth/logout',
    ME: '/v1/auth/me',
  },
  // Users
  USERS: {
    BASE: '/v1/users',
    ME: '/v1/users/me',
    STATS: '/v1/users/me/stats',
    CHANGE_PASSWORD: '/v1/users/me/change-password',
    AVATAR: '/v1/users/me/avatar',
  },
  // Companies
  COMPANIES: {
    BASE: '/v1/companies',
    MY: '/v1/companies/my',
    MEMBERS: (id: number) => `/v1/companies/${id}/members`,
    STATS: (id: number) => `/v1/companies/${id}/stats`,
    LOGO: (id: number) => `/v1/companies/${id}/logo`,
  },
  // Vacancies
  VACANCIES: {
    BASE: '/v1/vacancies',
    MY: '/v1/vacancies/my',
    MODERATION: '/v1/vacancies/moderation/pending',
    SIMILAR: (id: number) => `/v1/vacancies/${id}/similar`,
    RENEW: (id: number) => `/v1/vacancies/${id}/renew`,
  },
  // Resumes
  RESUMES: {
    BASE: '/v1/resumes',
    MY: '/v1/resumes/my',
    SCORE: (id: number) => `/v1/resumes/${id}/score`,
    COVER_LETTER: (id: number) => `/v1/resumes/${id}/cover-letter`,
    UPLOAD: (id: number) => `/v1/resumes/${id}/upload`,
  },
  // Applications
  APPLICATIONS: {
    BASE: '/v1/applications',
    MY: '/v1/applications/my',
    VACANCY: (id: number) => `/v1/applications/vacancy/${id}`,
    COMPANY: (id: number) => `/v1/applications/company/${id}`,
    STATUS: (id: number) => `/v1/applications/${id}/status`,
    STAGE: (id: number) => `/v1/applications/${id}/stage`,
    STAGES: (id: number) => `/v1/applications/${id}/stages`,
  },
  // Search
  SEARCH: {
    VACANCIES: '/v1/search/vacancies',
    RESUMES: '/v1/search/resumes',
    AUTOCOMPLETE: '/v1/search/autocomplete',
    SUGGESTIONS: '/v1/search/suggestions',
  },
  // Messages
  MESSAGES: {
    BASE: '/v1/messages',
    CONVERSATION: (id: number) => `/v1/messages/conversation/${id}`,
    CONVERSATIONS: '/v1/messages/conversations',
    UNREAD: '/v1/messages/unread/count',
    READ: (id: number) => `/v1/messages/${id}/read`,
    WS: (token: string) => `/ws/${token}`,
  },
  // Notifications
  NOTIFICATIONS: {
    BASE: '/v1/notifications',
    UNREAD: '/v1/notifications/unread/count',
    MARK_ALL_READ: '/v1/notifications/mark-all-read',
  },
  // Verification
  VERIFICATION: {
    INN_VALIDATE: '/v1/verification/inn/validate',
    INN_PRECHECK: '/v1/verification/inn/precheck',
    COMPANY_VERIFY: (id: number) => `/v1/verification/companies/${id}`,
    COMPANY_BATCH: '/v1/verification/companies/batch',
    HISTORY: (id: number) => `/v1/verification/companies/${id}/history`,
    STATS: '/v1/verification/statistics',
  },
  // Admin
  ADMIN: {
    STATS: '/v1/admin/stats',
    USERS: '/v1/admin/users',
    COMPANIES: '/v1/admin/companies',
    CACHE_CLEAR: '/v1/admin/cache/clear',
    SEARCH_REINDEX: '/v1/admin/search/reindex',
  },
  // Analytics
  ANALYTICS: {
    SALARY: '/v1/analytics/salary',
    VACANCY: (id: number) => `/v1/analytics/vacancy/${id}`,
    COMPANY: (id: number) => `/v1/analytics/company/${id}`,
    MARKET: '/v1/analytics/market',
  },
  // Health
  HEALTH: {
    BASE: '/v1/health',
    METRICS: '/v1/health/metrics',
  },
}

// ============================================
// Константы приложения
// ============================================

export const APP_NAME = 'LenJob'
export const APP_DESCRIPTION = 'Платформа для поиска работы и подбора персонала'
export const APP_VERSION = '2.0.0'

// ============================================
// Роли пользователей
// ============================================

export const USER_ROLES = {
  CANDIDATE: 'candidate',
  EMPLOYER: 'employer',
  ADMIN: 'admin',
} as const

export const USER_ROLE_LABELS: Record<string, string> = {
  candidate: 'Соискатель',
  employer: 'Работодатель',
  admin: 'Администратор',
}

// ============================================
// Статусы откликов
// ============================================

export const APPLICATION_STATUSES = {
  PENDING: 'pending',
  REVIEW: 'review',
  INVITED: 'invited',
  REJECTED: 'rejected',
  OFFER: 'offer',
  HIRED: 'hired',
} as const

export const APPLICATION_STATUS_LABELS: Record<string, string> = {
  pending: 'На рассмотрении',
  review: 'Просмотрено',
  invited: 'Приглашение на собеседование',
  rejected: 'Отказ',
  offer: 'Оффер',
  hired: 'Принят на работу',
}

export const APPLICATION_STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  review: 'bg-blue-100 text-blue-800',
  invited: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  offer: 'bg-purple-100 text-purple-800',
  hired: 'bg-emerald-100 text-emerald-800',
}

export const APPLICATION_STATUS_ICONS: Record<string, string> = {
  pending: '⏳',
  review: '👀',
  invited: '🎉',
  rejected: '😔',
  offer: '🎊',
  hired: '🥳',
}

// ============================================
// Этапы ATS (Канбан-доска)
// ============================================

export const ATS_STAGES = {
  NEW: 'new',
  INTERVIEW: 'interview',
  TEST: 'test',
  OFFER: 'offer',
  HIRED: 'hired',
  REJECTED: 'rejected',
} as const

export const ATS_STAGE_LABELS: Record<string, string> = {
  new: '🆕 Новые',
  interview: '🗣️ Интервью',
  test: '📝 Тестовое',
  offer: '🎯 Оффер',
  hired: '✅ Приняты',
  rejected: '❌ Отказ',
}

export const ATS_STAGE_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800',
  interview: 'bg-yellow-100 text-yellow-800',
  test: 'bg-purple-100 text-purple-800',
  offer: 'bg-green-100 text-green-800',
  hired: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
}

// ============================================
// Статусы модерации
// ============================================

export const MODERATION_STATUSES = {
  DRAFT: 'draft',
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const

export const MODERATION_STATUS_LABELS: Record<string, string> = {
  draft: 'Черновик',
  pending: 'На модерации',
  approved: 'Одобрено',
  rejected: 'Отклонено',
}

export const MODERATION_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

// ============================================
// Типы занятости
// ============================================

export const EMPLOYMENT_TYPES = {
  FULL_TIME: 'full_time',
  PART_TIME: 'part_time',
  PROJECT: 'project',
  INTERNSHIP: 'internship',
  REMOTE: 'remote',
} as const

export const EMPLOYMENT_TYPE_LABELS: Record<string, string> = {
  full_time: 'Полная занятость',
  part_time: 'Частичная занятость',
  project: 'Проектная работа',
  internship: 'Стажировка',
  remote: 'Удаленная работа',
}

// ============================================
// Графики работы
// ============================================

export const WORK_SCHEDULES = {
  FLEXIBLE: 'flexible',
  SHIFT: 'shift',
  REMOTE: 'remote',
  HYBRID: 'hybrid',
} as const

export const WORK_SCHEDULE_LABELS: Record<string, string> = {
  flexible: 'Гибкий график',
  shift: 'Сменный график',
  remote: 'Удаленная работа',
  hybrid: 'Гибридный график',
}

// ============================================
// Уровни опыта
// ============================================

export const EXPERIENCE_LEVELS = {
  NO_EXPERIENCE: 'no_experience',
  LESS_1_YEAR: 'less_1_year',
  FROM_1_TO_3: 'from_1_to_3',
  FROM_3_TO_6: 'from_3_to_6',
  MORE_6: 'more_6',
} as const

export const EXPERIENCE_LEVEL_LABELS: Record<string, string> = {
  no_experience: 'Без опыта',
  less_1_year: 'Менее 1 года',
  from_1_to_3: '1-3 года',
  from_3_to_6: '3-6 лет',
  more_6: 'Более 6 лет',
}

// ============================================
// Языки
// ============================================

export const LANGUAGES = {
  RU: 'ru',
  EN: 'en',
  KZ: 'kz',
  DE: 'de',
  FR: 'fr',
  ES: 'es',
  ZH: 'zh',
  AR: 'ar',
  HI: 'hi',
} as const

export const LANGUAGE_LABELS: Record<string, string> = {
  ru: 'Русский',
  en: 'English',
  kz: 'Қазақша',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  zh: '中文',
  ar: 'العربية',
  hi: 'हिन्दी',
}

export const LANGUAGE_FLAGS: Record<string, string> = {
  ru: '🇷🇺',
  en: '🇬🇧',
  kz: '🇰🇿',
  de: '🇩🇪',
  fr: '🇫🇷',
  es: '🇪🇸',
  zh: '🇨🇳',
  ar: '🇸🇦',
  hi: '🇮🇳',
}

// ============================================
// Типы уведомлений
// ============================================

export const NOTIFICATION_TYPES = {
  APPLICATION_UPDATE: 'application_update',
  NEW_MESSAGE: 'new_message',
  VACANCY_EXPIRING: 'vacancy_expiring',
  RESUME_VIEWED: 'resume_viewed',
} as const

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  application_update: 'Обновление отклика',
  new_message: 'Новое сообщение',
  vacancy_expiring: 'Истечение срока вакансии',
  resume_viewed: 'Просмотр резюме',
}

export const NOTIFICATION_TYPE_ICONS: Record<string, string> = {
  application_update: '📋',
  new_message: '💬',
  vacancy_expiring: '⏰',
  resume_viewed: '👁️',
}

// ============================================
// Параметры пагинации
// ============================================

export const DEFAULT_PAGE_SIZE = 20
export const MAX_PAGE_SIZE = 100
export const DEFAULT_PAGE = 1

// ============================================
// Параметры кэширования
// ============================================

export const CACHE_TIMES = {
  SHORT: 1 * 60 * 1000, // 1 минута
  MEDIUM: 5 * 60 * 1000, // 5 минут
  LONG: 30 * 60 * 1000, // 30 минут
  VERY_LONG: 24 * 60 * 60 * 1000, // 24 часа
}

// ============================================
// Форматы дат
// ============================================

export const DATE_FORMATS = {
  SHORT: 'dd.MM.yyyy',
  MEDIUM: 'dd MMM yyyy',
  LONG: 'dd MMMM yyyy',
  FULL: 'dd MMMM yyyy, HH:mm',
  ISO: 'yyyy-MM-dd',
  TIME: 'HH:mm',
  DATETIME: 'dd.MM.yyyy HH:mm',
}

// ============================================
// Валюты
// ============================================

export const CURRENCIES = {
  RUB: 'RUB',
  USD: 'USD',
  EUR: 'EUR',
  KZT: 'KZT',
} as const

export const CURRENCY_SYMBOLS: Record<string, string> = {
  RUB: '₽',
  USD: '$',
  EUR: '€',
  KZT: '₸',
}

// ============================================
// Размеры файлов
// ============================================

export const MAX_FILE_SIZE = {
  AVATAR: 5 * 1024 * 1024, // 5 MB
  RESUME: 10 * 1024 * 1024, // 10 MB
  COMPANY_LOGO: 5 * 1024 * 1024, // 5 MB
}

export const ALLOWED_FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
  RESUME: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
}

// ============================================
// Регулярные выражения
// ============================================

export const REGEX = {
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
  PHONE: /^\+?[0-9]{10,15}$/,
  INN_10: /^\d{10}$/,
  INN_12: /^\d{12}$/,
  URL: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
  SLUG: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d!@#$%^&*()_+]{8,}$/,
}

// ============================================
// Сообщения об ошибках
// ============================================

export const ERROR_MESSAGES = {
  NETWORK: 'Ошибка сети. Проверьте подключение к интернету.',
  SERVER: 'Внутренняя ошибка сервера. Пожалуйста, попробуйте позже.',
  UNAUTHORIZED: 'Сессия истекла. Пожалуйста, войдите заново.',
  FORBIDDEN: 'У вас нет прав для выполнения этого действия.',
  NOT_FOUND: 'Запрашиваемый ресурс не найден.',
  VALIDATION: 'Пожалуйста, проверьте введенные данные.',
  FILE_TOO_LARGE: 'Файл слишком большой.',
  FILE_INVALID: 'Неподдерживаемый тип файла.',
}

// ============================================
// Сообщения об успехе
// ============================================

export const SUCCESS_MESSAGES = {
  CREATED: 'Успешно создано',
  UPDATED: 'Успешно обновлено',
  DELETED: 'Успешно удалено',
  SAVED: 'Успешно сохранено',
  SENT: 'Успешно отправлено',
  VERIFIED: 'Успешно подтверждено',
  APPLIED: 'Отклик успешно отправлен',
}

// ============================================
// Meta-теги для SEO
// ============================================

export const META = {
  TITLE: 'LenJob — поиск работы и подбор персонала',
  DESCRIPTION: 'LenJob — современная платформа для поиска работы и подбора персонала с AI-функциями и ATS-системой.',
  KEYWORDS: 'поиск работы, подбор персонала, вакансии, резюме, HR, рекрутинг',
  OG_IMAGE: '/og-image.png',
  TWITTER_IMAGE: '/twitter-image.png',
}

// ============================================
// Роуты приложения
// ============================================

export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  VACANCIES: '/vacancies',
  VACANCY_CREATE: '/vacancies/create',
  VACANCY_EDIT: (id: number) => `/vacancies/${id}/edit`,
  VACANCY_DETAIL: (id: number) => `/vacancies/${id}`,
  RESUMES: '/resumes',
  RESUME_CREATE: '/resumes/create',
  RESUME_EDIT: (id: number) => `/resumes/${id}/edit`,
  RESUME_DETAIL: (id: number) => `/resumes/${id}`,
  COMPANIES: '/companies',
  COMPANY_CREATE: '/companies/create',
  COMPANY_EDIT: (id: number) => `/companies/${id}/edit`,
  COMPANY_DETAIL: (id: number) => `/companies/${id}`,
  APPLICATIONS: '/applications',
  MESSAGES: '/messages',
  NOTIFICATIONS: '/notifications',
  SETTINGS: '/settings',
  ANALYTICS: '/analytics',
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_COMPANIES: '/admin/companies',
  ADMIN_VACANCIES: '/admin/vacancies',
  ADMIN_VERIFICATION: '/admin/verification',
}

// ============================================
// Ключи для localStorage
// ============================================

export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
  USER: 'user',
  LANGUAGE: 'language',
  THEME: 'theme',
  SEARCH_HISTORY: 'search_history',
  RECENT_VIEWS: 'recent_views',
}

// ============================================
// Ключи для React Query
// ============================================

export const QUERY_KEYS = {
  USER: 'user',
  USERS: 'users',
  USER_STATS: 'user_stats',
  COMPANY: 'company',
  COMPANIES: 'companies',
  MY_COMPANIES: 'my_companies',
  VACANCY: 'vacancy',
  VACANCIES: 'vacancies',
  MY_VACANCIES: 'my_vacancies',
  RESUME: 'resume',
  RESUMES: 'resumes',
  MY_RESUMES: 'my_resumes',
  RESUME_SCORE: 'resume_score',
  APPLICATION: 'application',
  APPLICATIONS: 'applications',
  MY_APPLICATIONS: 'my_applications',
  SEARCH: 'search',
  SEARCH_VACANCIES: 'search_vacancies',
  SEARCH_RESUMES: 'search_resumes',
  AUTOCOMPLETE: 'autocomplete',
  SUGGESTIONS: 'suggestions',
  MESSAGES: 'messages',
  CONVERSATIONS: 'conversations',
  NOTIFICATIONS: 'notifications',
  UNREAD_COUNT: 'unread_count',
  VERIFICATION: 'verification',
  VERIFICATION_HISTORY: 'verification_history',
  ANALYTICS: 'analytics',
  SALARY_ANALYTICS: 'salary_analytics',
  ADMIN_STATS: 'admin_stats',
  HEALTH: 'health',
}

// ============================================
// Цветовая схема
// ============================================

export const COLORS = {
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  secondary: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  success: '#22c55e',
  warning: '#eab308',
  error: '#ef4444',
  info: '#3b82f6',
}

// ============================================
// Брейкпоинты для responsive
// ============================================

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

export const MEDIA_QUERIES = {
  sm: `(min-width: ${BREAKPOINTS.sm}px)`,
  md: `(min-width: ${BREAKPOINTS.md}px)`,
  lg: `(min-width: ${BREAKPOINTS.lg}px)`,
  xl: `(min-width: ${BREAKPOINTS.xl}px)`,
  '2xl': `(min-width: ${BREAKPOINTS['2xl']}px)`,
}
